/* GoldenLove — paw print cursor trail.

   Drops a paw behind the cursor, alternating left and right foot so it reads
   as a gait rather than a stamp. Each print scales in, holds, fades, and is
   removed from the DOM.

   Design notes:

   - Distance-gated, not time-gated. A print only appears after the pointer has
     travelled STEP px since the last one, so a fast flick and a slow drag lay
     down the same spacing, and an idle jitter lays down nothing.
   - One rAF loop drives every live print. Per-print timers or transitionend
     listeners would mean N timers and leaks on fast movement.
   - Hard cap on concurrent prints. A dropped frame or a very fast pointer must
     not be able to grow the DOM without bound.
   - pointer-events:none on the layer and every print, so this can never
     swallow a click or a text selection.
   - Skipped entirely for coarse pointers (no cursor to trail) and for anyone
     who prefers reduced motion.
*/

(function () {
  'use strict';

  var STEP = 30;        // px of travel between prints
  var LIFE = 900;       // ms from spawn to removal
  var GROW = 0.14;      // fraction of life spent scaling in
  var HOLD = 0.4;       // fraction of life held at full opacity
  var MAX = 24;         // concurrent prints
  var SIZE = 26;

  var PAW =
    '<svg viewBox="0 0 68 64" fill="currentColor" aria-hidden="true" focusable="false">' +
    '<ellipse cx="14" cy="27" rx="6.6" ry="8.8" transform="rotate(-24 14 27)"/>' +
    '<ellipse cx="27" cy="17" rx="7" ry="9.4" transform="rotate(-8 27 17)"/>' +
    '<ellipse cx="41" cy="17" rx="7" ry="9.4" transform="rotate(8 41 17)"/>' +
    '<ellipse cx="54" cy="27" rx="6.6" ry="8.8" transform="rotate(24 54 27)"/>' +
    '<path d="M34 33c8.5 0 15.5 4.4 17.6 10.6 2.1 6.3-1.9 12-8.6 13.2-3.4.6-5.9-.6-9-.6' +
    's-5.6 1.2-9 .6c-6.7-1.2-10.7-6.9-8.6-13.2C18.5 37.4 25.5 33 34 33z"/></svg>';

  function start() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (document.querySelector('.voffen-pawtrail')) return;

    var layer = document.createElement('div');
    layer.className = 'voffen-pawtrail';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);

    var prints = [];
    var lastX = null;
    var lastY = null;
    var foot = 1;          // flips each print: left paw, right paw
    var running = false;

    function spawn(x, y, angle) {
      var el = document.createElement('span');
      el.className = 'voffen-pawtrail__paw';
      el.innerHTML = PAW;

      // Offset perpendicular to travel so the two feet straddle the path.
      var perp = angle + Math.PI / 2;
      var stride = 7 * foot;

      el.style.left = x + Math.cos(perp) * stride + 'px';
      el.style.top = y + Math.sin(perp) * stride + 'px';

      var deg = angle * (180 / Math.PI) + 90;
      layer.appendChild(el);

      prints.push({ el: el, born: performance.now(), deg: deg });
      foot = -foot;

      // Oldest first — retire the excess rather than let the layer grow.
      while (prints.length > MAX) {
        var old = prints.shift();
        if (old.el.parentNode) old.el.parentNode.removeChild(old.el);
      }

      if (!running) {
        running = true;
        requestAnimationFrame(tick);
      }
    }

    function tick(now) {
      for (var i = prints.length - 1; i >= 0; i--) {
        var p = prints[i];
        var t = (now - p.born) / LIFE;

        if (t >= 1) {
          if (p.el.parentNode) p.el.parentNode.removeChild(p.el);
          prints.splice(i, 1);
          continue;
        }

        var scale;
        var opacity;

        if (t < GROW) {
          // Quick pop in, slightly overshooting full size.
          var g = t / GROW;
          scale = 0.4 + 0.75 * g;
          opacity = g;
        } else if (t < GROW + HOLD) {
          scale = 1;
          opacity = 1;
        } else {
          var f = (t - GROW - HOLD) / (1 - GROW - HOLD);
          scale = 1 - 0.18 * f;
          opacity = 1 - f;
        }

        // Clamp: a frame can land fractionally past the end before the
        // removal pass runs, which otherwise writes a negative opacity.
        if (opacity < 0) opacity = 0;
        else if (opacity > 1) opacity = 1;

        p.el.style.opacity = (opacity * 0.42).toFixed(3);
        p.el.style.transform =
          'rotate(' + p.deg.toFixed(1) + 'deg) scale(' + scale.toFixed(3) + ')';
      }

      if (prints.length) {
        requestAnimationFrame(tick);
      } else {
        running = false;
      }
    }

    window.addEventListener(
      'mousemove',
      function (event) {
        var x = event.clientX;
        var y = event.clientY;

        if (lastX === null) {
          lastX = x;
          lastY = y;
          return;
        }

        var dx = x - lastX;
        var dy = y - lastY;

        if (dx * dx + dy * dy < STEP * STEP) return;

        spawn(x, y, Math.atan2(dy, dx));
        lastX = x;
        lastY = y;
      },
      { passive: true }
    );

    // Leaving the window would otherwise strand the last print mid-fade on
    // return, with a stale origin for the next gait.
    document.addEventListener('mouseleave', function () {
      lastX = null;
      lastY = null;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
